import { api } from './config.js';
import { processApiData } from './utils.js';

/**
 * Get information about a token
 * @param {string} tokenAddress - Token contract address
 * @returns {Promise<Object|null>} - Token information or null if not found
 */
const getTokenInfo = async (tokenAddress) => {
  try {
    console.log(`Fetching token info for: ${tokenAddress}`);
    const response = await api.get(`/tokens/${tokenAddress}`);
    return processApiData(response.data);
  } catch (error) {
    console.error('Error in getTokenInfo:', error);
    return null;
  }
};

/**
 * Get token transfers
 * @param {string} tokenAddress - Token contract address
 * @param {number} limit - Maximum number of transfers to return
 * @param {number} offset - Number of transfers to skip
 * @returns {Promise<Array>} - Array of token transfers
 */
const getTokenTransfers = async (tokenAddress, limit = 10, offset = 0) => {
  try {
    console.log(`Fetching token transfers for token: ${tokenAddress}`);
    const response = await api.get(`/tokens/${tokenAddress}/transfers`, {
      params: { limit, offset }
    });
    const data = processApiData(response.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error in getTokenTransfers:', error);
    return [];
  }
};

/**
 * Get token holders
 * @param {string} tokenAddress - Token contract address
 * @param {number} limit - Maximum number of holders to return
 * @param {number} offset - Number of holders to skip
 * @returns {Promise<Object>} - Object with holders array, totalHolders count, and tokenInfo
 */
const getTokenHolders = async (tokenAddress, limit = 100, offset = 0) => {
  try {
    console.log(`Fetching token holders for token: ${tokenAddress}`);
    const response = await api.get(`/tokens/${tokenAddress}/holders`, {
      params: { limit, offset }
    });
    const data = processApiData(response.data);
    
    // The API returns an array of holders, we need to format it properly
    if (Array.isArray(data)) {
      // Get token info to provide additional context
      let tokenInfo = null;
      try {
        const tokenResponse = await api.get(`/tokens/${tokenAddress}`);
        tokenInfo = processApiData(tokenResponse.data);
      } catch (tokenError) {
        console.warn('Could not fetch token info:', tokenError);
      }
      
      // Format holders with proper balance formatting
      const formattedHolders = data.map(holder => ({
        address: holder.address,
        balance: holder.balance,
        balanceFormatted: holder.balance ? (parseFloat(holder.balance) / Math.pow(10, tokenInfo?.decimals || 18)).toString() : '0',
        percentage: holder.percentage || 0
      }));
      
      return {
        holders: formattedHolders,
        totalHolders: tokenInfo?.holders || formattedHolders.length, // Use actual holder count from token info
        tokenInfo: tokenInfo ? {
          name: tokenInfo.name,
          symbol: tokenInfo.symbol,
          decimals: tokenInfo.decimals || 18
        } : null
      };
    }
    
    return { holders: [], totalHolders: 0, tokenInfo: null };
  } catch (error) {
    console.error('Error in getTokenHolders:', error);
    return { holders: [], totalHolders: 0, tokenInfo: null };
  }
};

/**
 * Get NFT collections
 * @param {number} limit - Maximum number of collections to return
 * @param {number} offset - Number of collections to skip
 * @returns {Promise<Array>} - Array of NFT collections
 */
const getNftCollections = async (limit = 10, offset = 0) => {
  try {
    console.log('Fetching NFT collections');
    const response = await api.get('/nfts', {
      params: { limit, offset }
    });
    const data = processApiData(response.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error in getNftCollections:', error);
    return [];
  }
};

/**
 * Get NFT collection details
 * @param {string} tokenAddress - NFT contract address
 * @returns {Promise<Object|null>} - NFT collection details or null if not found
 */
const getNftCollection = async (tokenAddress) => {
  try {
    console.log(`Fetching NFT collection for address: ${tokenAddress}`);
    const response = await api.get(`/nfts/${tokenAddress}`);
    return processApiData(response.data);
  } catch (error) {
    console.error(`Error in getNftCollection for ${tokenAddress}:`, error);
    return null;
  }
};

/**
 * Get NFT token details
 * @param {string} tokenAddress - NFT contract address
 * @param {string} tokenId - NFT token ID
 * @returns {Promise<Object|null>} - NFT token details or null if not found
 */
const getNftToken = async (tokenAddress, tokenId) => {
  try {
    console.log(`Fetching NFT token: ${tokenAddress}/${tokenId}`);
    const response = await api.get(`/nfts/${tokenAddress}/${tokenId}`);
    return processApiData(response.data);
  } catch (error) {
    console.error(`Error in getNftToken for ${tokenAddress}/${tokenId}:`, error);
    return null;
  }
};

export {
  getTokenInfo,
  getTokenTransfers,
  getTokenHolders,
  getNftCollections,
  getNftCollection,
  getNftToken
};
